import React, { useEffect, useRef, useState } from "react";
import { getErrorMessage } from "../utils/errors";

type Facing = "user" | "environment";

interface Props {
  onCapture: (blob: Blob) => void;
}

export default function CameraCapture({ onCapture }: Props): React.JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [facing, setFacing] = useState<Facing>("user");
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string>("");

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => { previewUrlRef.current = previewUrl; }, [previewUrl]);

  const stopStream = () => {
    const stream = streamRef.current;
    if (stream) {
      for (const t of stream.getTracks()) t.stop();
    }
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setRunning(false);
  };

  useEffect(() => {
    return () => {
      stopStream();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
     
  }, []);

  const start = async (dir: Facing = facing) => {
    if (!videoRef.current || starting) return;
    setError("");
    setStarting(true);
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: dir }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setRunning(true);
      setFacing(dir);
    } catch (error_) {
      setError(getErrorMessage(error_, "Could not access camera."));
    } finally {
      setStarting(false);
    }
  };

  const switchCamera = async () => { await start(facing === "user" ? "environment" : "user"); };

  const capture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) =>
      { canvas.toBlob((b) => { resolve(b); }, "image/jpeg", 0.92); }
    );
    if (!blob) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
    setPreviewBlob(blob);
  };

  const usePhoto = () => {
    if (previewBlob) onCapture(previewBlob);
    stopStream();
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
  };

  return (
    <div className="grid gap-3">
      {/* Preview (live video or captured image) */}
      <div
        className="relative overflow-hidden bg-black"
        style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--rule)' }}
      >
        {!previewUrl && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`block w-full aspect-[3/4] object-cover ${facing === "user" ? "scale-x-[-1]" : ""}`}
          />
        )}
        {previewUrl && (
          <img src={previewUrl} alt="Captured preview" className="block w-full aspect-[3/4] object-cover" />
        )}
        {/* Face guide */}
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div
            className="w-[70%] h-[70%]"
            style={{ borderRadius: 24, border: '2px solid rgba(255,255,255,0.4)' }}
          />
        </div>
      </div>

      {/* Controls */}
      {!running && !previewUrl && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => start()} disabled={starting} className="btn btn--primary btn--sm">
            {starting ? "Starting…" : "Start camera"}
          </button>
        </div>
      )}

      {running && !previewUrl && (
        <div className="flex flex-wrap gap-2">
          <button onClick={capture} className="btn btn--primary btn--sm">Capture</button>
          <button onClick={switchCamera} className="btn btn--ghost btn--sm">Switch camera</button>
          <button onClick={stopStream} className="btn btn--ghost btn--sm">Stop</button>
        </div>
      )}

      {previewUrl && (
        <div className="flex flex-wrap gap-2">
          <button onClick={usePhoto} className="btn btn--primary btn--sm">Use photo</button>
          <button onClick={retake} className="btn btn--ghost btn--sm">Retake</button>
        </div>
      )}

      {!!error && (
        <div
          className="text-sm px-3 py-2"
          style={{
            background: 'rgba(244,168,142,0.15)',
            border: '1px solid var(--c-coral)',
            borderRadius: 'var(--r-md)',
            color: 'var(--c-charcoal)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
